import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from '../components/Navbar';
import opLinuxImg from '../op_Linux_img.png';
import opWebwasImg from '../op_WEBWAS_img.png';
import opDbmsImg from '../op_DBMS_img.png';
import { addMobileDetectionListener } from '../utils/deviceDetection';
import '../App.css';

const OpenSourcePage = () => {
  const location = useLocation();
  const [activeCategory, setActiveCategory] = useState('linux-os');
  const [isMobile, setIsMobile] = useState(false);
  
  // 각 섹션에 대한 ref 생성
  const linuxOsRef = useRef(null);
  const webWasRef = useRef(null);
  const dbmsRef = useRef(null);

  const categories = [
    { id: 'linux-os', name: 'Linux OS', title: 'Linux 운영체제' },
    { id: 'web-was', name: 'WEB/WAS', title: 'Web Application Server' },
    { id: 'dbms', name: 'DBMS', title: '데이터베이스 관리 시스템' }
  ];

  const refs = {
    'linux-os': linuxOsRef,
    'web-was': webWasRef,
    'dbms': dbmsRef
  };

  // 모바일 감지 설정
  useEffect(() => {
    const cleanupMobileListener = addMobileDetectionListener(setIsMobile);
    return () => {
      cleanupMobileListener();
    };
  }, []);

  // URL 파라미터 확인하여 초기 카테고리 설정
  useEffect(() => {
    // 페이지 로드 시 스크롤을 맨 위로 이동
    window.scrollTo(0, 0);
    
    const params = new URLSearchParams(location.search);
    const category = params.get('category');
    if (category && categories.find(cat => cat.id === category)) {
      setActiveCategory(category);
      // 페이지 로드 후 해당 섹션으로 스크롤
      setTimeout(() => {
        const targetRef = refs[category];
        if (targetRef && targetRef.current) {
          const headerOffset = isMobile ? 70 : 88;
          const elementPosition = targetRef.current.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, [location.search, isMobile]);

  // Intersection Observer 설정
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-20% 0px -70% 0px', // 상단 20%, 하단 70% 위치에서 트리거
      threshold: 0
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          // 현재 보이는 섹션의 카테고리 ID 찾기
          const categoryId = Object.keys(refs).find(key => 
            refs[key].current === entry.target
          );
          if (categoryId) {
            setActiveCategory(categoryId);
          }
        }
      });
    }, observerOptions);

    // 모든 섹션을 관찰 대상으로 등록
    Object.values(refs).forEach(ref => {
      if (ref.current) {
        observer.observe(ref.current);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  const handleCategoryClick = (categoryId) => {
    setActiveCategory(categoryId);
    const targetRef = refs[categoryId];
    if (targetRef && targetRef.current) {
      // 상단 고정 헤더 높이 (68px) + 여유 공간 (20px) = 88px
      const headerOffset = isMobile ? 70 : 88;
      const elementPosition = targetRef.current.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className={`subcustom-header-container ${isMobile ? 'mobile-device' : ''}`}>
      <header className="subcustom-header">
        <div className="hero-title-area">
          <div className="hero-subtitle">OPEN SOURCE</div>
          <h2 className="hero-title">오픈소스</h2>
        </div>
      </header>

      <main className="infra-main">
        {/* 모바일에서는 카테고리 탭 숨김 */}

        <div className="solutions-layout">
          {/* 왼쪽 사이드바 카테고리 메뉴 - 데스크톱에서만 표시 */}
          {!isMobile && (
            <div className="sidebar-categories">
              <h3 className="sidebar-title">오픈소스</h3>
              <ul className="category-menu">
                {categories.map(category => (
                  <li key={category.id}>
                    <button
                      className={`category-item ${activeCategory === category.id ? 'active' : ''}`}
                      onClick={() => handleCategoryClick(category.id)}
                    >
                      {category.name}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* 오른쪽 콘텐츠 영역 */}
          <div className="content-area">
            {/* Linux OS 섹션 */}
            <div className="solution-section" ref={linuxOsRef}>
              <h2 className="section-title">Linux OS</h2>
              <div className="product-content">
                <div className="product-text">
                  <p>다양한 Linux 배포판과 관련 오픈소스 솔루션을 기술지원하고 있습니다.</p>
                  <div className="feature-list">
                    <h3>지원 제품:</h3>
                    <ul>
                      <li><strong>Red Hat Enterprise Linux</strong> - 엔터프라이즈급 안정성과 보안을 제공하는 상용 Linux 배포판</li>
                      <li><strong>Rocky Linux</strong> - CentOS의 후속으로 등장한 엔터프라이즈급 무료 Linux 배포판</li>
                      <li><strong>Ubuntu LTS</strong> - 장기 지원(Long Term Support)을 제공하는 안정적인 Linux 배포판</li>
                      <li><strong>Oracle Linux</strong> - Oracle에서 제공하는 엔터프라이즈급 Linux 배포판</li>
                    </ul>
                  </div>
                </div>
                <div className="product-image">
                  <img src={opLinuxImg} alt="Linux OS" />
                </div>
              </div>
            </div>

            {/* Web/WAS 섹션 */}
            <div className="solution-section" ref={webWasRef}>
              <h2 className="section-title">WEB/WAS</h2>
              <div className="product-content">
                <div className="product-text">
                  <p>확장 가능하고 안정적인 웹 애플리케이션 서버 솔루션을 기술지원하고 있습니다.</p>
                  <div className="feature-list">
                    <h3>지원 제품:</h3>
                    <ul>
                      <li><strong>Apache Tomcat</strong> - Java 서블릿과 JSP를 위한 오픈소스 웹 애플리케이션 서버</li>
                      <li><strong>NginX</strong> - 고성능 웹 서버이자 리버스 프록시 서버</li>
                      <li><strong>Apache Httpd Server</strong> - 세계에서 가장 널리 사용되는 웹 서버</li>
                      <li><strong>Wildfly</strong> - JBoss AS의 후속작인 Java EE 애플리케이션 서버</li>
                    </ul>
                  </div>
                </div>
                <div className="product-image">
                  <img src={opWebwasImg} alt="Web Application Server" />
                </div>
              </div>
            </div>

            {/* DBMS 섹션 */}
            <div className="solution-section" ref={dbmsRef}>
              <h2 className="section-title">DBMS 및 HA솔루션</h2>
              <div className="product-content">
                <div className="product-text">
                  <p>오픈소스 기반의 강력하고 안정적인 데이터베이스 솔루션을 기술지원하고 있습니다.</p>
                  <div className="feature-list">
                    <h3>지원 제품:</h3>
                    <ul>
                      <li><strong>MariaDBMS</strong> - MySQL의 오픈소스 포크로 향상된 성능과 기능 제공</li>
                      <li><strong>PostgreSQL</strong> - 고급 기능을 제공하는 객체-관계형 데이터베이스 시스템</li>
                      <li><strong>MySQL</strong> - 세계에서 가장 널리 사용되는 오픈소스 관계형 데이터베이스</li>
                      <li><strong>AgensSQL</strong> - 그래프 데이터베이스와 관계형 데이터베이스의 장점을 결합</li>
                      <li><strong>MaxScale 이중화 구성</strong> - MariaDB/MySQL을 위한 고가용성 프록시 솔루션</li>
                      <li><strong>Galera Cluster/MaxScale 이중화 구성</strong> - 멀티 마스터 클러스터 구성</li>
                    </ul>
                  </div>
                </div>
                <div className="product-image">
                  <img src={opDbmsImg} alt="Database Management System" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default OpenSourcePage; 